/**
 * Generate Python/R code for the preprocessing pipeline
 * This allows users to reproduce their preprocessing steps programmatically
 */

export const generatePythonCode = (operations) => {
  let code = `# Data Preprocessing Pipeline
# Generated by UoA COMPSCI 714 Data Preprocessing Studio
# Date: ${new Date().toISOString()}

import pandas as pd
import numpy as np
from sklearn.preprocessing import StandardScaler, MinMaxScaler
from sklearn.impute import SimpleImputer

# Load your data
df = pd.read_csv('your_data.csv')

print(f"Original shape: {df.shape}")
print(f"Missing values:\\n{df.isnull().sum()}")

`;

  operations.forEach((op, index) => {
    code += `\n# Step ${index + 1}: ${op.description}\n`;
    
    switch(op.type) {
      case 'imputation':
        code += generateImputationCode(op);
        break;
      case 'scaling':
        code += generateScalingCode(op);
        break;
      case 'encoding':
        code += generateEncodingCode(op);
        break;
      case 'feature_engineering':
        code += generateFeatureEngineeringCode(op);
        break;
      default:
        code += `# ${op.type} operation\n`;
    }
  });

  code += `\n# Save processed data
df.to_csv('processed_data.csv', index=False)
print(f"\\nProcessed shape: {df.shape}")
print("\\nProcessing complete!")
`;

  return code;
};

const generateImputationCode = (op) => {
  const { column, strategy } = op;
  
  switch(strategy) {
    case 'mean':
      return `df['${column}'].fillna(df['${column}'].mean(), inplace=True)\n`;
    case 'median':
      return `df['${column}'].fillna(df['${column}'].median(), inplace=True)\n`;
    case 'mode':
      return `df['${column}'].fillna(df['${column}'].mode()[0], inplace=True)\n`;
    case 'forward':
      return `df['${column}'].fillna(method='ffill', inplace=True)\n`;
    case 'backward':
      return `df['${column}'].fillna(method='bfill', inplace=True)\n`;
    case 'zero':
      return `df['${column}'].fillna(0, inplace=True)\n`;
    case 'custom':
      return `df['${column}'].fillna(${op.value}, inplace=True)\n`;
    default:
      return `# Imputation for ${column}\n`;
  }
};

const generateScalingCode = (op) => {
  const { columns, method } = op;
  const colList = columns.map(c => `'${c}'`).join(', ');
  
  if (method === 'standardization') {
    return `scaler = StandardScaler()
df[[${colList}]] = scaler.fit_transform(df[[${colList}]])
`;
  } else {
    return `scaler = MinMaxScaler()
df[[${colList}]] = scaler.fit_transform(df[[${colList}]])
`;
  }
};

const generateEncodingCode = (op) => {
  const { column, method } = op;
  
  if (method === 'label') {
    return `from sklearn.preprocessing import LabelEncoder
le = LabelEncoder()
df['${column}_encoded'] = le.fit_transform(df['${column}'])
`;
  } else {
    return `df = pd.get_dummies(df, columns=['${column}'], prefix='${column}')
`;
  }
};

const generateFeatureEngineeringCode = (op) => {
  const { col1, col2, operation, newFeature } = op;
  
  const ops = {
    'add': '+',
    'subtract': '-',
    'multiply': '*',
    'divide': '/',
    'ratio': '/'
  };
  
  return `df['${newFeature}'] = df['${col1}'] ${ops[operation]} df['${col2}']
`;
};

export const generateRCode = (operations) => {
  let code = `# Data Preprocessing Pipeline
# Generated by UoA COMPSCI 714 Data Preprocessing Studio
# Date: ${new Date().toISOString()}

library(dplyr)
library(tidyr)

# Load your data
df <- read.csv('your_data.csv')

cat("Original shape:", dim(df), "\\n")
cat("Missing values:\\n")
print(colSums(is.na(df)))

`;

  operations.forEach((op, index) => {
    code += `\n# Step ${index + 1}: ${op.description}\n`;
    
    switch(op.type) {
      case 'imputation':
        code += generateRImputationCode(op);
        break;
      case 'scaling':
        code += generateRScalingCode(op);
        break;
      case 'encoding':
        code += generateREncodingCode(op);
        break;
      case 'feature_engineering':
        code += generateRFeatureEngineeringCode(op);
        break;
      default:
        code += `# ${op.type} operation\n`;
    }
  });

  code += `\n# Save processed data
write.csv(df, 'processed_data.csv', row.names = FALSE)
cat("\\nProcessed shape:", dim(df), "\\n")
cat("Processing complete!\\n")
`;

  return code;
};

const generateRImputationCode = (op) => {
  const { column, strategy } = op;
  
  switch(strategy) {
    case 'mean':
      return `df$${column}[is.na(df$${column})] <- mean(df$${column}, na.rm = TRUE)\n`;
    case 'median':
      return `df$${column}[is.na(df$${column})] <- median(df$${column}, na.rm = TRUE)\n`;
    case 'mode':
      return `mode_val <- names(sort(table(df$${column}), decreasing = TRUE))[1]
df$${column}[is.na(df$${column})] <- mode_val\n`;
    case 'forward':
      return `df <- df %>% fill(${column}, .direction = "down")\n`;
    case 'backward':
      return `df <- df %>% fill(${column}, .direction = "up")\n`;
    case 'zero':
      return `df$${column}[is.na(df$${column})] <- 0\n`;
    default:
      return `# Imputation for ${column}\n`;
  }
};

const generateRScalingCode = (op) => {
  const { columns, method } = op;
  
  if (method === 'standardization') {
    return `df[c(${columns.map(c => `"${c}"`).join(', ')})] <- scale(df[c(${columns.map(c => `"${c}"`).join(', ')})])
`;
  } else {
    return `normalize <- function(x) { (x - min(x, na.rm = TRUE)) / (max(x, na.rm = TRUE) - min(x, na.rm = TRUE)) }
df[c(${columns.map(c => `"${c}"`).join(', ')})] <- lapply(df[c(${columns.map(c => `"${c}"`).join(', ')})], normalize)
`;
  }
};

const generateREncodingCode = (op) => {
  const { column, method } = op;
  
  if (method === 'label') {
    return `df$${column}_encoded <- as.numeric(factor(df$${column}))
`;
  } else {
    return `df <- df %>% 
  mutate(dummy = 1) %>%
  pivot_wider(names_from = ${column}, values_from = dummy, values_fill = 0, names_prefix = "${column}_")
`;
  }
};

const generateRFeatureEngineeringCode = (op) => {
  const { col1, col2, operation, newFeature } = op;
  
  const ops = {
    'add': '+',
    'subtract': '-',
    'multiply': '*',
    'divide': '/',
    'ratio': '/'
  };
  
  return `df$${newFeature} <- df$${col1} ${ops[operation]} df$${col2}
`;
};

export const downloadCode = (code, filename) => {
  const blob = new Blob([code], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
